import { useState, useCallback } from 'react';

/**
 * Hook to handle usage limit errors from the backend
 * 
 * Usage:
 * const { handleApiError, modals, closeModal } = useUsageLimits();
 * 
 * try {
 *   await createUser(data);
 * } catch (error) {
 *   handleApiError(error);
 * }
 */
export function useUsageLimits() {
  const [modals, setModals] = useState({
    userLimit: { isOpen: false, details: null },
    leadLimit: { isOpen: false, details: null },
    featureUnavailable: { isOpen: false, details: null }
  });

  /**
   * Handle API errors and show appropriate modals
   */
  const handleApiError = useCallback((error) => {
    // Check if error response has our structured error format
    const errorDetail = error?.response?.data?.detail;
    
    if (!errorDetail || typeof errorDetail !== 'object') {
      // Not a structured error, let it bubble up
      throw error;
    }

    const { error_code, message, details } = errorDetail;

    switch (error_code) {
      case 'USER_LIMIT_REACHED':
        setModals(prev => ({
          ...prev,
          userLimit: {
            isOpen: true,
            details: {
              current_users: details.current_users,
              max_users: details.max_users,
              plan: details.plan,
              extra_user_price: 4 // Could also come from details
            }
          }
        }));
        break;

      case 'LEAD_LIMIT_REACHED':
        setModals(prev => ({
          ...prev,
          leadLimit: {
            isOpen: true,
            details: {
              current_leads: details.current_leads,
              max_leads: details.max_leads,
              available: details.available,
              leads_to_add: details.leads_to_add || 1,
              plan: details.plan,
              topup_available: details.topup_available,
              topup_price: getPlanTopupPrice(details.plan),
              topup_size: 100
            }
          }
        }));
        break;

      case 'AI_INTRO_MAIL_NOT_AVAILABLE_FOR_PLAN':
        setModals(prev => ({
          ...prev,
          featureUnavailable: {
            isOpen: true,
            details: {
              current_plan: details.current_plan,
              feature: details.feature || 'AI Intro Mail',
              required_plan: details.required_plan || 'Tier 1 or higher'
            }
          }
        }));
        break;

      default:
        // Not a recognized error code, let it bubble up
        throw error;
    }
  }, []);

  /**
   * Close a specific modal
   */
  const closeModal = useCallback((modalName) => {
    setModals(prev => ({
      ...prev,
      [modalName]: { isOpen: false, details: null }
    }));
  }, []);

  /**
   * Close all modals
   */
  const closeAllModals = useCallback(() => {
    setModals({
      userLimit: { isOpen: false, details: null },
      leadLimit: { isOpen: false, details: null },
      featureUnavailable: { isOpen: false, details: null }
    });
  }, []);

  return {
    handleApiError,
    modals,
    closeModal,
    closeAllModals
  };
}

/**
 * Helper function to get top-up price based on plan
 */
function getPlanTopupPrice(plan) {
  const prices = {
    base: 19,
    tier1: 19,
    tier2: 20
  };
  return prices[plan] || 19;
}

/**
 * Check if an error is a usage limit error
 */
export function isUsageLimitError(error) {
  const errorDetail = error?.response?.data?.detail;
  
  if (!errorDetail || typeof errorDetail !== 'object') {
    return false;
  }

  const recognizedCodes = [
    'USER_LIMIT_REACHED',
    'LEAD_LIMIT_REACHED',
    'AI_INTRO_MAIL_NOT_AVAILABLE_FOR_PLAN'
  ];

  return recognizedCodes.includes(errorDetail.error_code);
}
